import { QueryParameter, RouterOrigins } from '@deltasierra/frontend-routing-core';
import { BuilderTemplateId, LocationId } from '@deltasierra/ids';
import { ViewFileDto } from '@deltasierra/microservice-clients-asset-library';
import { BuilderDocument, BuilderEmailDocument, BuilderPrintDocument, BuilderDocumentFormat } from '../builderDocument';
import { ClientId } from '../client';
import { FullAuditable, HasId, Omit, assertNever } from '../common';
import { PathBuilder, ApiRoute, HttpMethod } from '../common/api/routes';
import { Html, ProtocolRelativeUrl, SignedUrl } from '../common/lang/web';
import { Tag } from '../tag';
import { Upload } from '../upload';
import { BuilderTemplateCategory, BuilderTemplateCategoryId } from './BuilderTemplateCategory';
import { BasicBuilderTemplateFormat, BuilderTemplateFormat } from './BuilderTemplateFormat';

/**
 * @deprecated
 * Instead use
 * ```
 * import { BuilderTemplateId } from '@deltasierra/ids';
 * ```
 */
export { BuilderTemplateId };

interface BaseBuilderTemplateProperties extends HasId<BuilderTemplateId>, FullAuditable {
    title: string;
    documentFormat: BuilderDocumentFormat;
    documentFormatVersion: string;
    document: BuilderDocument | BuilderEmailDocument | BuilderPrintDocument;
    clientId: ClientId;
    compositeImageId: number | null;
    editableText: boolean;
    editableImage: boolean;
    editableVideo: boolean;
    isDraft: boolean;
    mobile: boolean;
    isMultiImage: boolean;
    currentRevisionId: string | null;
    buildableTemplateId: string | null;
}

export type BuilderType = 'contentBuilder' | 'emailBuilder';

export interface BuilderTemplate extends BaseBuilderTemplateProperties {
    tags?: Tag[];
    formats?: BuilderTemplateFormat[];
    categories?: BuilderTemplateCategory[];
    compositeImage?: Upload;
    graphqlId: string;
}

export type ContentBuilderTemplate = BuilderTemplate & {
    document: BuilderDocument | BuilderPrintDocument;
};

interface BuilderTemplateBasicAssociations {
    formats: BasicBuilderTemplateFormat[];
    categories: Array<Pick<BuilderTemplateCategory, 'id' | 'title'>>;
}

export type BuilderTemplateAdditionalData = {
    browser: string;
    locationId?: LocationId;
    timestamp: string;
    timezone: string;
};

export type BuilderTemplateWithBasicAssociations = BuilderTemplateBasicAssociations &
    Omit<BuilderTemplate, 'categories' | 'formats'>;

export type ContentBuilderTemplateWithBasicAssociations = BuilderTemplateWithBasicAssociations & {
    document: BuilderDocument | BuilderPrintDocument;
};

export type BuilderTemplateGalleryDto = Omit<
    BuilderTemplateWithBasicAssociations,
    'createdById' | 'deletedAt' | 'deletedById' | 'document' | 'documentFormatVersion' | 'updatedById'
> & { multiImageCount?: number };

export interface TemplateGroupItem
    extends Omit<
        BuilderTemplateGalleryDto,
        | 'compositeImage'
        | 'compositeImageId'
        | 'editableImage'
        | 'editableText'
        | 'editableVideo'
        | 'formats'
        | 'mobile'
        | 'updatedAt'
    > {
    hasRelatedDraft?: boolean;
    compositeImage: { thumb256x256url: ProtocolRelativeUrl } | null;
    formats: Array<Omit<BasicBuilderTemplateFormat, 'animatable' | 'canPublishTo' | 'height' | 'type' | 'width'>>;
    multiImageCount?: number;
}

export interface TemplateGroupItemWithSignedUrl extends Omit<TemplateGroupItem, 'compositeImage'> {
    compositeImage: { thumb256x256url: SignedUrl } | null;
}

export type ImageMap = { [key: string]: Upload };

export type DocumentTemplateToSave = Pick<
    Partial<BuilderTemplate>,
    | 'documentFormat'
    | 'documentFormatVersion'
    | 'editableImage'
    | 'editableText'
    | 'editableVideo'
    | 'id'
    | 'mobile'
    | 'title'
> &
    Pick<BuilderTemplate, 'clientId' | 'document' | 'formats' | 'isDraft' | 'isMultiImage' | 'tags'> & {
        compositeImageUpload: Upload | null;
        imageMap: ImageMap;
        type?: 'email' | 'image' | 'print';
        categories: BuilderTemplateCategoryId[];
        linkedAssetLibraryAssetIds: Array<{ assetId: number; layerId: number }>;
    };

export type DocumentTemplateToSaveWithAdditionalData = DocumentTemplateToSave & {
    additionalData?: BuilderTemplateAdditionalData | null | undefined;
};

export type TemplateMetadataDto = Pick<BuilderTemplate, 'title'> & {
    categories: BuilderTemplateCategoryId[];
    tags: number[];
};

export interface EditableFieldsFilter {
    text: boolean;
    image: boolean;
    video: boolean;
}

export interface AgencyOptionsFilter {
    allClients: boolean;
    hideDraft: boolean;
    hideMobile: boolean;
    multiImage: boolean;
    hidePublished: boolean;
    onlyLocationDraft: boolean;
    onlyMobile: boolean;
}

export interface GetAllBuilderTemplatesQuery {
    formats?: number[];
    categories?: BuilderTemplateCategoryId[];
    uncategorised?: boolean;
    recent?: boolean;
    location?: LocationId;
    client?: ClientId;
    tags?: string;
    exactTag?: string;
    limit?: number;
    previousDate?: Date;
    previousId?: number;
    platforms?: number[];
    editableFields?: Partial<EditableFieldsFilter>;
    onlyLocationDraft?: boolean;
    hideDraft?: boolean;
    hidePublished?: boolean;
    mobile?: boolean;
    multiImage?: boolean;
}

export function linkToTemplate(
    documentFormat: BuilderDocumentFormat,
    templateId: number,
    plannerId?: number,
    fitwareMemberEngagerAutomatedEmailCode?: string,
    collectionId?: number,
): string {
    let path;
    switch (documentFormat) {
        case BuilderDocumentFormat.email:
            path = '/emailBuilder';
            break;

        case BuilderDocumentFormat.print:
        case BuilderDocumentFormat.content:
        case BuilderDocumentFormat.video:
            path = '/contentBuilder';
            break;

        default:
            throw assertNever(documentFormat);
    }

    path += `?template=${templateId}`;

    if (plannerId) {
        path += `&planner=${plannerId}`;
    }
    if (fitwareMemberEngagerAutomatedEmailCode) {
        path += `&automatedEmailCode=${fitwareMemberEngagerAutomatedEmailCode}`;
    }
    if (collectionId) {
        path += `&collection=${collectionId}`;
    }
    return path;
}

export function linkToBuildableTemplatePublish(
    documentFormat: BuilderDocumentFormat,
    templateId: string,
    clientId: string,
    locationId: string,
    frontendUrl?: string,
): string {
    let path = `${frontendUrl}/publish`;
    switch (documentFormat) {
        case BuilderDocumentFormat.email:
            path += '/email';
            break;
        case BuilderDocumentFormat.print:
        case BuilderDocumentFormat.content:
        case BuilderDocumentFormat.video:
            // These template types have not been implemented yet and are not used.
            return path;
        default:
            throw assertNever(documentFormat);
    }

    path += `/?${new URLSearchParams({
        [QueryParameter.ClientId]: clientId,
        [QueryParameter.LocationId]: locationId,
        [QueryParameter.TemplateId]: templateId,
        [QueryParameter.Origin]: RouterOrigins.OLD_TEMPLATE_GALLERY,
    })}`;

    return path;
}

type LinkToBuildableTemplateBuilderOptions = {
    documentFormat: BuilderDocumentFormat;
    templateId: string;
    clientId: string;
    frontendUrl: string;
};
export function linkToBuildableTemplateBuilder({
    clientId,
    documentFormat,
    frontendUrl,
    templateId,
}: LinkToBuildableTemplateBuilderOptions): string {
    let path = `${frontendUrl}/manage/templates`;
    switch (documentFormat) {
        case BuilderDocumentFormat.email:
            path += '/email';
            break;
        case BuilderDocumentFormat.print:
        case BuilderDocumentFormat.content:
        case BuilderDocumentFormat.video:
            /*
             * These template types have not been implemented yet and are not used.
             * TODO: These should throw not return the base path
             */
            break;
        default:
            throw assertNever(documentFormat);
    }
    const templateSearchParams = new URLSearchParams({
        [QueryParameter.ClientId]: `${clientId}`,
        [QueryParameter.Origin]: `${RouterOrigins.OLD_TEMPLATE_GALLERY}`,
        [QueryParameter.TemplateId]: templateId,
    });

    path += `/?${templateSearchParams.toString()}`;
    return path;
}

export const DEFAULT_GALLERY_CHUNK_SIZE = 36;
export const TEMPLATES_PER_CATEGORY_CHUNK_SIZE = 8;

const BUILDER_TEMPLATES_COLLECTION_API_ROOT = (path: PathBuilder<void>) => path.literal('builderTemplates');
const BUILDER_TEMPLATE_API_ROOT = (path: PathBuilder<BuilderTemplateBaseParams>) =>
    path.literal('builderTemplates').param('templateId');
const LOCATION_DRAFT_API_ROOT = (path: PathBuilder<LocationDraftBaseParams>) =>
    path.literal('locationDrafts').param('locationDraftGraphqlId');

interface BuilderTemplateBaseParams {
    templateId: BuilderTemplateId;
}

interface LocationDraftBaseParams {
    locationDraftGraphqlId: string;
}

export interface TemplateDraftModeDto {
    isDraft: boolean;
}

export interface TakeHtmlScreenshotRequest {
    html: Html;
}

export interface LegacyAssetIdAndLayerId {
    layerId: number;
    legacyAssetId: number;
}

export interface AssetIdAndLayerId {
    assetId: number;
    layerId: number;
}

export interface AssetAndLayerId {
    layerId: number;
    asset: Pick<ViewFileDto, 'id' | 'mimeType' | 'url'>;
}

export interface BuilderTemplateInfo {
    createdBy: string | null;
    updatedBy: string | null;
    createdAt: Date;
    updatedAt: Date | null;
}

export interface UpdateBuilderTemplateQueryParams {
    deleteLocationDrafts?: boolean;
}

export const BuilderTemplateApi = {
    createBuilderTemplate: new ApiRoute<
        BuilderTemplateWithBasicAssociations,
        void,
        DocumentTemplateToSaveWithAdditionalData,
        void
    >(HttpMethod.Post, BUILDER_TEMPLATES_COLLECTION_API_ROOT),
    deleteBuilderTemplate: new ApiRoute<{ numDeleted: number }, BuilderTemplateBaseParams, void, void>(
        HttpMethod.Delete,
        BUILDER_TEMPLATE_API_ROOT,
    ),
    getAllAssetLibraryAssetsUsedInLocationDraft: new ApiRoute<AssetAndLayerId[], LocationDraftBaseParams, void, void>(
        HttpMethod.Get,
        path => LOCATION_DRAFT_API_ROOT(path).literal('assets'),
    ),
    getAllAssetLibraryAssetsUsedInTemplate: new ApiRoute<AssetAndLayerId[], BuilderTemplateBaseParams, void, void>(
        HttpMethod.Get,
        path => BUILDER_TEMPLATE_API_ROOT(path).literal('assets'),
    ),
    getAllBuilderTemplates: new ApiRoute<BuilderTemplateGalleryDto[], void, void, GetAllBuilderTemplatesQuery>(
        HttpMethod.Get,
        BUILDER_TEMPLATES_COLLECTION_API_ROOT,
    ),
    getBuilderTemplate: new ApiRoute<BuilderTemplateWithBasicAssociations, BuilderTemplateBaseParams, void, void>(
        HttpMethod.Get,
        BUILDER_TEMPLATE_API_ROOT,
    ),
    getBuilderTemplateInfo: new ApiRoute<BuilderTemplateInfo, BuilderTemplateBaseParams, void, void>(
        HttpMethod.Get,
        path => BUILDER_TEMPLATE_API_ROOT(path).literal('info'),
    ),
    getLocationDraftAsBuilderTemplate: new ApiRoute<
        BuilderTemplateWithBasicAssociations,
        LocationDraftBaseParams,
        void,
        void
    >(HttpMethod.Get, LOCATION_DRAFT_API_ROOT),
    getSuggested: new ApiRoute<BuilderTemplateWithBasicAssociations[], { locationId: LocationId }, void, void>(
        HttpMethod.Get,
        path => path.literal('builderTemplates').literal('suggested').param('locationId'),
    ),
    takeHtmlScreenshot: new ApiRoute<Upload, void, TakeHtmlScreenshotRequest, void>(HttpMethod.Post, path =>
        path // TODO: maybe move this endpoint elsewhere?
            .literal('screenshotHtml'),
    ),
    updateBuilderTemplate: new ApiRoute<
        BuilderTemplateWithBasicAssociations,
        BuilderTemplateBaseParams,
        DocumentTemplateToSaveWithAdditionalData,
        UpdateBuilderTemplateQueryParams
    >(HttpMethod.Post, BUILDER_TEMPLATE_API_ROOT),
    updateDraftMode: new ApiRoute<
        BuilderTemplateWithBasicAssociations,
        BuilderTemplateBaseParams,
        TemplateDraftModeDto,
        void
    >(HttpMethod.Patch, path => path.literal('builderTemplates').param('templateId').literal('draft')),
    updateMetadata: new ApiRoute<
        BuilderTemplateWithBasicAssociations,
        BuilderTemplateBaseParams,
        TemplateMetadataDto,
        void
    >(HttpMethod.Put, BUILDER_TEMPLATE_API_ROOT),
};
